import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  FaMapMarkerAlt,
  FaBuilding,
  FaLayerGroup,
  FaDoorOpen,
  FaImage,
  FaUpload,
  FaTrash,
  FaDrawPolygon,
  FaCog,
  FaSpinner
} from 'react-icons/fa';
import {
  selectSelectedObject,
  updateObject,
  setSelectedObject,
  setSelectedArea,
  refreshAreas,
  updateArea
} from '../../../actions/infoTerminal';
import {
  uploadImageToServer,
  deleteImageFromServer,
  fetchImageBlob,
  fetchObjectDetails
} from '../../../actions/infoTerminalApi';
import styles from './styles.module.scss';
import ConfirmModal from '../Delete';

const ICONS = {
  territory: FaMapMarkerAlt,
  building: FaBuilding,
  floor: FaLayerGroup,
  room: FaDoorOpen
};

const TYPE_LABELS = {
  territory: 'Территория',
  building: 'Здание',
  floor: 'Этаж',
  room: 'Аудитория'
};

const PropertiesPanel = ({
  selectedObject,
  selectedAreaId,
  allObjects,
  pendingAreaUpdates,
  hasChanges,
  areasRefreshCount,
  updateObject
}) => {
  const dispatch = useDispatch();
  // Состояние формы объекта
  const [objectForm, setObjectForm] = useState({
    name: '',
    description: '',
    isActive: true
  });

  // Состояние формы области
  const [areaForm, setAreaForm] = useState({
    linkedObjectId: '',
    isActive: true
  });

  // Состояние модалки удаления и загрузки
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);

  // Загружаем детали объекта (включая области) с сервера
  const [objectDetails, setObjectDetails] = useState(null);

  // Вычисляем selectedArea из objectDetails по selectedAreaId
  const selectedArea = objectDetails?.areas?.find(a => a.id === selectedAreaId) || null;

  // Загрузка деталей объекта при выборе объекта или области, или после сохранения
  useEffect(() => {
    if (selectedObject?.id) {
      dispatch(fetchObjectDetails(selectedObject.id)).then(details => {
        if (details) {
          setObjectDetails(details);
        }
      });
    } else {
      setObjectDetails(null);
    }
  }, [selectedObject?.id, selectedAreaId, areasRefreshCount, dispatch]);

  // Загрузка изображения с сервера при выборе объекта
  useEffect(() => {
    let currentBlobUrl = null;

    if (selectedObject?.has_image && selectedObject?.id) {
      setImageBlobUrl(null);
      setImageLoading(true);
      dispatch(fetchImageBlob(selectedObject.id, 'preview')).then(url => {
        currentBlobUrl = url;
        setImageBlobUrl(url);
        setImageLoading(false);
      });
    } else {
      setImageBlobUrl(null);
    }

    // Cleanup: освобождаем blob URL при смене объекта
    return () => {
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [selectedObject?.id, selectedObject?.has_image, dispatch]);

  const confirmDelete = async () => {
    if (deleteConfirm && selectedObject) {
      setImageLoading(true);
      await dispatch(deleteImageFromServer(selectedObject.id));
      setImageBlobUrl(null);
      setImageLoading(false);
      setDeleteConfirm(null);
    }
  };

  const handleDelete = (item, e) => {
    e.stopPropagation();
    setDeleteConfirm(item);
  };

  // Синхронизация формы объекта с выбранным объектом
  useEffect(() => {
    if (selectedObject) {
      setObjectForm({
        name: selectedObject.name || '',
        description: selectedObject.description || '',
        isActive: selectedObject.isActive !== false
      });
    }
  }, [selectedObject?.id]);

  // Синхронизация формы области с выбранной областью (учитывая локальные изменения)
  useEffect(() => {
    if (selectedArea) {
      // Проверяем есть ли локальные изменения для этой области
      const pendingUpdate = pendingAreaUpdates[selectedArea.id];
      setAreaForm({
        linkedObjectId: pendingUpdate?.target_object_id !== undefined
          ? (pendingUpdate.target_object_id || '')
          : (selectedArea.target_object_id || ''),
        isActive: selectedArea.is_active !== false
      });
    }
  }, [selectedArea?.id, selectedArea?.target_object_id, pendingAreaUpdates]);

  // Обработчики изменения полей объекта
  const handleObjectChange = (field, value) => {
    setObjectForm(prev => ({ ...prev, [field]: value }));
  };

  const handleObjectBlur = (field) => {
    if (selectedObject && objectForm[field] !== selectedObject[field]) {
      updateObject(selectedObject.id, { [field]: objectForm[field] });
    }
  };

  // Обработчики изменения полей области (локально, сохранение по кнопке)
  const handleAreaChange = (field, value) => {
    setAreaForm(prev => ({ ...prev, [field]: value }));
    if (selectedArea) {
      // Маппинг полей формы на поля API
      const apiField = field === 'linkedObjectId' ? 'target_object_id' : field;
      // Локальное обновление в Redux (сохранится по кнопке "Сохранить")
      dispatch(updateArea(selectedArea.id, { [apiField]: value || null }));
      // Уведомляем другие компоненты об изменении областей
      dispatch(refreshAreas());
    }
  };

  // Загрузка изображения на сервер
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file && selectedObject) {
      setImageLoading(true);
      const result = await dispatch(uploadImageToServer(selectedObject.id, file));
      if (result) {
        // Освобождаем старый blob URL и загружаем новое изображение без кэша
        if (imageBlobUrl) {
          URL.revokeObjectURL(imageBlobUrl);
          setImageBlobUrl(null);
        }
        const url = await dispatch(fetchImageBlob(selectedObject.id, 'preview'));
        setImageBlobUrl(url);
      }
      setImageLoading(false);
    }
    e.target.value = '';
  };

  // Получаем дочерние объекты для привязки области
  const getChildrenForLinking = () => {
    if (!selectedObject) return [];
    return allObjects.filter(obj => obj.parentId === selectedObject.id);
  };

  // Пустое состояние
  if (!selectedObject) {
    return (
      <div className={styles.panelContainer}>
        <div className={styles.header}>
          <h3>Свойства</h3>
        </div>
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <FaCog />
            <p>Выберите объект для редактирования</p>
          </div>
        </div>
      </div>
    );
  }


  // Форма области
  if (selectedArea) {
    const children = getChildrenForLinking();

    return (
      <div className={styles.panelContainer}>
        <div className={styles.header}>
          <h3>Свойства области</h3>
          <p className={styles.subtitle}>Объект: {selectedObject.name}</p>
        </div>
        <div className={styles.content}>
          <div className={styles.areaInfo}>
            <div className={styles.pointsCount}>
              <FaDrawPolygon style={{ marginRight: 6 }} />
              {selectedArea.polygon?.length || 0} точек
            </div>
          </div>

          <div className={styles.formGroup}>
            <label>Привязанный объект</label>
            <select
              value={areaForm.linkedObjectId}
              onChange={(e) => handleAreaChange('linkedObjectId', e.target.value)}
            >
              <option value="">— Не привязано —</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>
                  {TYPE_LABELS[child.type]}: {child.name}
                </option>
              ))}
            </select>
            
          </div>

          {children.length === 0 && (
            <p style={{ fontSize: 12, color: '#999', marginTop: -8 }}>
              У объекта нет дочерних элементов для привязки
            </p>
          )}

          <div className={styles.divider} />
        </div>
      </div>
    );
  }

  // Форма объекта
  const Icon = ICONS[selectedObject.type] || FaMapMarkerAlt;

  return (
    <div className={styles.panelContainer}>
      <ConfirmModal
          isOpen={!!deleteConfirm}
          title="Подтверждение удаления"
          message={
            <>
              Вы уверены, что хотите удалить <strong> изображение "{deleteConfirm?.name}"</strong>?
              {deleteConfirm?.children?.length > 0 && (
                <><br /><br />Все дочерние объекты также будут удалены.</>
              )}
            </>
          }
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      <div className={styles.header}>
        <h3>Свойства объекта</h3>
      </div>
      <div className={styles.content}>
        <div className={styles.formGroup}>
          <label>Тип</label>
          <div className={styles.typeLabel}>
            <Icon size={14} />
            <span>{TYPE_LABELS[selectedObject.type]}</span>
          </div>
        </div>

        {selectedObject.type === 'building' && selectedObject.external_building?.floors_raw != null && (
          <div className={styles.formGroup}>
            <label>Этажей в здании</label>
            <div className={styles.typeLabel}>
              <FaLayerGroup size={14} />
              <span>{selectedObject.external_building.floors_raw}</span>
            </div>
          </div>
        )}

        <div className={styles.formGroup}>
          <label htmlFor="objName">Название</label>
          {selectedObject.type === 'room' ? (
            <input
              id="objName"
              type="text"
              value={selectedObject.external_room?.name || objectForm.name}
              readOnly
              placeholder="Не привязано"
            />
          ) : (
            <input
              id="objName"
              type="text"
              value={objectForm.name}
              onChange={(e) => handleObjectChange('name', e.target.value)}
              onBlur={() => handleObjectBlur('name')}
              placeholder="Введите название"
            />
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="objDesc">Описание</label>
          <textarea
            id="objDesc"
            value={objectForm.description}
            onChange={(e) => handleObjectChange('description', e.target.value)}
            onBlur={() => handleObjectBlur('description')}
            placeholder="Введите описание (необязательно)"
          />
        </div>

        <div className={styles.imageSection}>
          <div className={styles.formGroup}>
            <label>Изображение схемы</label>
            <div className={styles.imagePreview}>
              {imageLoading ? (
                <FaSpinner className={styles.spinner} />
              ) : imageBlobUrl ? (
                <img src={imageBlobUrl} alt="Schema" />
              ) : (
                <FaImage />
              )}
            </div>
            <div className={styles.imageActions}>
              <label className={`${styles.uploadBtn} ${imageLoading ? styles.disabled : ''}`}>
                <FaUpload size={12} />
                <span>{selectedObject.has_image ? 'Заменить' : 'Загрузить'}</span>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleImageUpload}
                  disabled={imageLoading}
                />
              </label>
              {selectedObject.has_image && (
                <button
                  className={styles.deleteBtn}
                  onClick={(e) => handleDelete(selectedObject, e)}
                  disabled={imageLoading || selectedObject.areas_count > 0}
                  title={selectedObject.areas_count > 0 ? 'Сначала удалите области' : 'Удалить изображение'}
                >
                  <FaTrash size={12} />
                  <span>Удалить</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {objectDetails?.areas && objectDetails.areas.length > 0 && (
          <>
            <div className={styles.divider} />
            <div className={styles.formGroup}>
              <label>Области на схеме</label>
              <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
                {objectDetails.areas.length} {
                  objectDetails.areas.length === 1 ? 'область' :
                  objectDetails.areas.length < 5 ? 'области' : 'областей'
                }
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const selectedObject = selectSelectedObject(state);
  return {
    selectedObject,
    selectedAreaId: state.infoTerminal.selectedAreaId,
    allObjects: state.infoTerminal.objects,
    pendingAreaUpdates: state.infoTerminal.pendingAreaUpdates,
    hasChanges: state.infoTerminal.hasChanges,
    areasRefreshCount: state.infoTerminal.areasRefreshCount
  };
};

export default connect(mapStateToProps, {
  updateObject,
  setSelectedObject,
  setSelectedArea
})(PropertiesPanel);